/**
 * slugify.js
 * 
 * Utilities for creating SEO-friendly URL slugs.
 * Converts titles to URL-safe strings and extracts IDs from slugified URLs.
 * 
 * @example
 * import { slugify, getIdFromSlug, buildVideoUrl } from './utils/slugify';
 * 
 * slugify('My Video Title!'); // 'my-video-title'
 * getIdFromSlug('my-video-title'); // null
 * getIdFromSlug('12345'); // '12345'
 * buildVideoUrl('12345', 'My Video'); // '/details/12345/my-video'
 */

/**
 * Convert a string to a URL-safe slug
 * 
 * @param {string} text - Text to slugify
 * @param {Object} options - Configuration options
 * @param {number} options.maxLength - Maximum slug length (default: 50)
 * @param {boolean} options.lowercase - Convert to lowercase (default: true)
 * @returns {string} - URL-safe slug
 */
export const slugify = (text, options = {}) => {
  if (!text) return '';

  const { maxLength = 50, lowercase = true } = options;

  let slug = text
    .toString()
    .trim()
    // Remove accents/diacritics
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove special characters
    .replace(/[^\w\-]+/g, '')
    // Replace multiple hyphens with single hyphen
    .replace(/\-\-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+/, '')
    .replace(/-+$/, '');

  if (lowercase) {
    slug = slug.toLowerCase();
  }

  // Truncate to max length
  if (slug.length > maxLength) {
    slug = slug.substring(0, maxLength);
    // Remove trailing hyphen if truncation created one
    slug = slug.replace(/-+$/, '');
  }

  return slug;
};

/**
 * Extract ID from a slug or path
 * Handles various formats:
 * - '/details/12345/my-video-title'
 * - '12345/my-video-title'
 * - '12345'
 * 
 * @param {string} slug - Slug or path to extract ID from
 * @returns {string|null} - Extracted ID or null
 */
export const getIdFromSlug = (slug) => {
  if (!slug) return null;

  // Remove leading/trailing slashes
  const cleaned = slug.toString().trim().replace(/^\/+|\/+$/g, '');

  // Split by /
  const parts = cleaned.split('/');

  // Try to find a part that looks like an ID
  for (const part of parts) {
    // Check if it's a valid MongoDB ObjectId (24 hex chars)
    if (/^[0-9a-fA-F]{24}$/.test(part)) {
      return part;
    }
    // Check if it's a numeric ID
    if (/^\d+$/.test(part)) {
      return part;
    }
  }

  // If no ID found in parts, return the first part
  // This handles the case where the slug is just an ID
  if (parts.length > 0 && parts[0]) {
    return parts[0];
  }

  return null;
};

/**
 * Build a video detail URL with slug
 * 
 * @param {string} id - Video ID
 * @param {string} title - Video title
 * @returns {string} - Full URL path
 */
export const buildVideoUrl = (id, title) => {
  if (!id) return '/';

  const slug = slugify(title || 'video');
  return `/${id}/${slug}`;
};

/**
 * Build a full shareable URL for a video (always uses production domain)
 * @param {string} id - Video ID
 * @param {string} title - Video title
 * @returns {string} - Full absolute URL
 */
export const buildShareUrl = (id, title) => {
  const base = process.env.REACT_APP_URL || 'https://iburrow.com';
  return `${base}${buildVideoUrl(id, title)}`;
};

/**
 * Build a channel URL with slug
 * 
 * @param {string} id - Channel ID
 * @param {string} name - Channel name
 * @returns {string} - Full URL path
 */
export const buildChannelUrl = (id, name) => {
  if (!id) return '/channel';

  const slug = slugify(name || 'channel');
  return `/channel/${id}/${slug}`;
};

/**
 * Parse a video URL and extract ID and slug
 * 
 * @param {string} url - URL to parse
 * @returns {Object} - Object with id and slug properties
 */
export const parseVideoUrl = (url) => {
  if (!url) return { id: null, slug: null };

  // Remove leading/trailing slashes
  const cleaned = url.toString().trim().replace(/^\/+|\/+$/g, '');

  // Split by /
  const parts = cleaned.split('/');

  // Format 1 (legacy): details/ID/SLUG
  if (parts[0] === 'details' && parts.length >= 2) {
    return {
      id: parts[1],
      slug: parts[2] || null
    };
  }

  // Format 2 (clean): ID/SLUG — ID looks like a UUID or mongo ObjectId
  if (parts.length >= 1 && /^[0-9a-f-]{8,}$/i.test(parts[0])) {
    return {
      id: parts[0],
      slug: parts[1] || null
    };
  }

  // Fallback: try to extract ID
  const id = getIdFromSlug(url);
  return {
    id,
    slug: null
  };
};

/**
 * Check if a slug is valid (contains only allowed characters)
 * 
 * @param {string} slug - Slug to validate
 * @returns {boolean} - True if valid
 */
export const isValidSlug = (slug) => {
  if (!slug) return false;
  // Allow lowercase letters, numbers, and hyphens
  // No leading/trailing hyphens
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
};

/**
 * Generate a unique slug by appending a number if needed
 * 
 * @param {string} baseSlug - Base slug to make unique
 * @param {Function} checkExists - Function to check if slug exists (async)
 * @returns {Promise<string>} - Unique slug
 * 
 * @example
 * const slug = await makeUniqueSlug('my-video', async (slug) => {
 *   const video = await db.videos.findOne({ slug });
 *   return !!video;
 * });
 */
export const makeUniqueSlug = async (baseSlug, checkExists) => {
  let slug = baseSlug;
  let counter = 1;

  while (await checkExists(slug)) {
    slug = `${baseSlug}-${counter}`;
    counter++;
  }

  return slug;
};

/**
 * Compare two slugs for equality (case-insensitive)
 * 
 * @param {string} slug1 - First slug
 * @param {string} slug2 - Second slug
 * @returns {boolean} - True if equal
 */
export const slugsEqual = (slug1, slug2) => {
  if (!slug1 || !slug2) return false;
  return slug1.toLowerCase() === slug2.toLowerCase();
};

export default {
  slugify,
  getIdFromSlug,
  buildVideoUrl,
  buildShareUrl,
  buildChannelUrl,
  parseVideoUrl,
  isValidSlug,
  makeUniqueSlug,
  slugsEqual
};
